import {
  Badge,
  Button,
  DatePicker,
  EmptyState,
  FormField,
  Icon,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  Textarea,
  todayIso,
  useToast,
  type KeyValueItem,
  type MenuItem,
} from '@ui';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage, formErrors } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { useLocalColumnSort } from '../../../lib/useColumnSort';
import { useInvoices, useSales } from '../../sales/api';
import { InvoiceBadge } from '../../sales/components/SaleBadges';
import { SaleFormDialog } from '../../sales/components/SaleFormDialog';
import { SalesTable } from '../../sales/components/SalesTable';
import { formatEuro } from '../../sales/format';
import { TasksCard } from '../../tasks/components/TasksCard';
import {
  useCustomer,
  useCustomerAction,
  useCustomerNotes,
  useDeleteCustomer,
  useDeleteCustomerNote,
  useSaveCustomerNote,
  type Customer,
  type CustomerNote,
} from '../api';
import { CustomerFormDialog } from '../components/CustomerFormDialog';
import styles from './CrmPages.module.css';

type Open = 'edit' | 'sale' | 'anonymize' | 'delete' | null;

/** A customer with purchases, invoices and contact history (CUS-004 – CUS-006). */
export function CustomerDetailPage() {
  const { t } = useTranslation();
  const { customerId = '' } = useParams();
  const customer = useCustomer(customerId);
  if (customer.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(customer.error)}
        actions={<BackLink to="/kunden" label={t('crm.title')} />}
      />
    );
  }
  if (!customer.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <CustomerDetail customer={customer.data} />;
}

function CustomerDetail({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<Open>(null);
  const action = useCustomerAction();
  const remove = useDeleteCustomer();
  // Paged: the server sorts (#188).
  const salesSort = useLocalColumnSort();
  const sales = useSales(
    { customer_id: customer.id, sort: salesSort.value || undefined },
    can('sales.read'),
  );
  const invoices = useInvoices({ customer_id: customer.id }, can('invoices.read'));
  const close = () => {
    action.reset();
    remove.reset();
    setOpen(null);
  };
  const stats = customer.stats;
  const anonymized = customer.anonymized_at != null;
  const archived = customer.archived_at != null;
  const saleRows = sales.data?.pages.flatMap((p) => p.items) ?? [];

  // Archiving is reversible, so it happens at once with an undo (§6.10).
  const setArchived = (value: boolean) =>
    action.mutate(
      { id: customer.id, action: value ? 'archive' : 'unarchive' },
      {
        onSuccess: () =>
          toast.show(
            value ? t('crm.detail.archived') : t('crm.detail.unarchived'),
            'success',
            value
              ? {
                  label: t('common.undo'),
                  onClick: () => action.mutate({ id: customer.id, action: 'unarchive' }),
                }
              : undefined,
          ),
        onError: (e) => toast.show(errorMessage(e), 'danger'),
      },
    );

  const more: MenuItem[] = can('customers.write')
    ? [
        ...(!anonymized
          ? [{ key: 'edit', label: t('common.edit'), onSelect: () => setOpen('edit') }]
          : []),
        {
          key: 'archive',
          label: archived ? t('crm.detail.unarchive') : t('crm.detail.archive'),
          onSelect: () => setArchived(!archived),
        },
        ...(!anonymized
          ? [
              {
                key: 'anonymize',
                label: t('crm.detail.anonymize'),
                tone: 'danger' as const,
                separated: true,
                onSelect: () => setOpen('anonymize'),
              },
            ]
          : []),
        {
          key: 'delete',
          label: t('common.delete'),
          tone: 'danger' as const,
          separated: anonymized,
          onSelect: () => setOpen('delete'),
        },
      ]
    : [];

  const openItems = stats
    ? [
        stats.open_orders > 0
          ? { key: 'orders', text: t('crm.detail.openOrders', { count: stats.open_orders }) }
          : null,
        stats.open_invoices > 0
          ? {
              key: 'invoices',
              text: t('crm.detail.openInvoices', { count: stats.open_invoices }),
            }
          : null,
        stats.overdue_invoices > 0
          ? {
              key: 'overdue',
              text: t('crm.detail.overdueInvoices', { count: stats.overdue_invoices }),
              danger: true,
            }
          : null,
      ].filter((x) => x !== null)
    : [];

  const salutation =
    customer.salutation !== 'none' ? t(`crm.salutation.${customer.salutation}`) : '';
  const contactItems: KeyValueItem[] = [
    {
      key: 'salutation',
      label: t('crm.form.salutation'),
      value: customer.kind === 'person' ? salutation || null : null,
    },
    {
      key: 'contactPerson',
      label: t('crm.form.contactPerson'),
      value: customer.contact_person && `${salutation} ${customer.contact_person}`.trim(),
    },
    {
      key: 'address',
      label: t('crm.form.address'),
      value: customer.address && (
        <>
          {customer.address.street}
          <br />
          {customer.address.postal_code} {customer.address.city}
          {customer.address.country && customer.address.country !== 'DE' && (
            <> · {customer.address.country}</>
          )}
        </>
      ),
    },
    {
      key: 'email',
      label: t('crm.form.email'),
      value: customer.email && <a href={`mailto:${customer.email}`}>{customer.email}</a>,
    },
    {
      key: 'phone',
      label: t('crm.form.phone'),
      value: customer.phone && <a href={`tel:${customer.phone}`}>{customer.phone}</a>,
    },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/kunden" label={t('crm.title')} />}
        title={customer.name}
        meta={
          <>
            {t(`crm.kind.${customer.kind}`)}
            {archived && (
              <>
                {' · '}
                <Badge>{t('crm.archived')}</Badge>
              </>
            )}
            {anonymized && (
              <>
                {' · '}
                <Badge>{t('crm.anonymized')}</Badge>
              </>
            )}
          </>
        }
        actions={
          <>
            {can('sales.write') && !archived && !anonymized && (
              <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setOpen('sale')}>
                {t('sales.quick.sale')}
              </Button>
            )}
            {more.length > 0 && <MoreMenu label={t('common.moreActions')} items={more} />}
          </>
        }
      />

      <div className={styles.columns}>
        <div className={styles.side}>
          <Section
            title={t('crm.detail.contact')}
            footer={
              customer.changed.at &&
              (customer.changed.by
                ? t('apiary.colony.changed', {
                    date: formatDateTime(customer.changed.at),
                    name: customer.changed.by,
                  })
                : t('apiary.colony.changedNoName', { date: formatDateTime(customer.changed.at) }))
            }
          >
            {contactItems.every((i) => !i.value) ? (
              <Text tone="muted" className={styles.empty}>
                {t('crm.detail.noContact')}
              </Text>
            ) : (
              <KeyValue items={contactItems} />
            )}
          </Section>
          {stats && (
            <Section title={t('crm.detail.revenue')}>
              <KeyValue
                items={[
                  {
                    key: 'open',
                    label: t('crm.detail.open'),
                    value: openItems.length > 0 && (
                      <span>
                        {openItems.map((o, i) => (
                          <Fragment key={o.key}>
                            {i > 0 && ' · '}
                            <span className={o.danger ? styles.danger : undefined}>{o.text}</span>
                          </Fragment>
                        ))}
                      </span>
                    ),
                  },
                  {
                    key: 'year',
                    label: t('crm.stats.revenueYear'),
                    value: formatEuro(stats.revenue_year_cents),
                  },
                  {
                    key: 'total',
                    label: t('crm.stats.revenueTotal'),
                    value: formatEuro(stats.revenue_total_cents),
                  },
                  {
                    key: 'purchases',
                    label: t('crm.stats.purchases'),
                    value: stats.purchases,
                  },
                  {
                    key: 'last',
                    label: t('crm.stats.lastPurchase'),
                    value: stats.last_purchase ? formatDate(stats.last_purchase) : null,
                  },
                ]}
              />
            </Section>
          )}
          {(customer.top_varieties.length > 0 || customer.notes) && (
            <Section title={t('crm.detail.preferences')}>
              <KeyValue
                items={[
                  {
                    key: 'top',
                    label: t('crm.detail.topVarieties'),
                    // One variety per line, so the list wraps cleanly on phones (#60).
                    value:
                      customer.top_varieties.length > 0 ? (
                        <span className={styles.lines}>
                          {customer.top_varieties.map((v) => (
                            <span key={v.variety.id}>
                              {t('crm.detail.varietyJars', { name: v.variety.name, count: v.jars })}
                            </span>
                          ))}
                        </span>
                      ) : null,
                  },
                  {
                    key: 'notes',
                    label: t('crm.form.notes'),
                    value: customer.notes && <span className={styles.notes}>{customer.notes}</span>,
                  },
                ]}
              />
            </Section>
          )}
        </div>

        <div className={styles.main}>
          {can('sales.read') && (
            <Section title={t('crm.detail.sales')} variant="flush">
              <SalesTable
                sort={salesSort.sort}
                onSortChange={salesSort.onSortChange}
                presorted
                caption={t('crm.detail.sales')}
                rows={saleRows}
                loading={sales.isPending}
                hideCustomer
                empty={
                  <Text tone="muted" className={styles.inset}>
                    {t('crm.detail.noSales')}
                  </Text>
                }
              />
              {sales.hasNextPage && (
                <div className={styles.more}>
                  <Button
                    variant="plain"
                    size="sm"
                    loading={sales.isFetchingNextPage}
                    onClick={() => void sales.fetchNextPage()}
                  >
                    {t('sales.list.more')}
                  </Button>
                </div>
              )}
            </Section>
          )}
          {can('invoices.read') && (invoices.data?.length ?? 0) > 0 && (
            <Section title={t('crm.detail.invoices')}>
              <List>
                {(invoices.data ?? []).map((i) => (
                  <ListRow
                    key={i.id}
                    href={`/verkauf/rechnungen/${i.id}`}
                    title={i.number ?? t('sales.invoice.badge.draft')}
                    description={i.issue_date ? formatDate(i.issue_date) : undefined}
                    trailing={
                      <>
                        {formatEuro(i.total_cents)}
                        <InvoiceBadge
                          status={i.status}
                          paid={i.paid}
                          overdue={i.overdue}
                          cancellation={i.kind === 'cancellation'}
                        />
                      </>
                    }
                  />
                ))}
              </List>
            </Section>
          )}
          <ContactHistory customer={customer} />
          <TasksCard
            filter={{ customer_id: customer.id }}
            context={{ customerId: customer.id, label: customer.name }}
          />
        </div>
      </div>

      {open === 'edit' && <CustomerFormDialog open customer={customer} onClose={close} />}
      {open === 'sale' && (
        <SaleFormDialog
          open
          customerId={customer.id}
          onClose={close}
          onSaved={(s) => void navigate(`/verkauf/${s.id}`)}
        />
      )}
      <ConfirmDialog
        open={open === 'anonymize'}
        onClose={close}
        title={t('crm.detail.anonymizeTitle', { name: customer.name })}
        description={t('crm.detail.anonymizeText')}
        confirmLabel={t('crm.detail.anonymize')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          action.mutate(
            { id: customer.id, action: 'anonymize' },
            {
              onSuccess: () => {
                toast.show(t('crm.detail.anonymizedToast'));
                close();
              },
            },
          )
        }
      />
      <ConfirmDialog
        open={open === 'delete'}
        onClose={close}
        title={t('crm.detail.deleteTitle', { name: customer.name })}
        description={t('crm.detail.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          remove.mutate(customer.id, {
            onSuccess: () => {
              toast.show(t('crm.detail.deleted', { name: customer.name }));
              void navigate('/kunden');
            },
          })
        }
      />
    </div>
  );
}

/** Contact history: calls, wishes, complaints (CUS-005). */
function ContactHistory({ customer }: { customer: Customer }) {
  const { t } = useTranslation();
  const rowMenu = useRowMenu();
  const toast = useToast();
  const { can } = useSession();
  const notes = useCustomerNotes(customer.id);
  const save = useSaveCustomerNote();
  const remove = useDeleteCustomerNote();
  const [text, setText] = useState('');
  const [on, setOn] = useState(todayIso());
  const [deleting, setDeleting] = useState<CustomerNote | null>(null);
  const errors = formErrors(save.error, { text: ['customer_note.text', 'note'] });
  const writable = can('customers.write') && customer.anonymized_at == null;
  const rows = notes.data ?? [];
  const submit = () =>
    save.mutate(
      { customerId: customer.id, text, noted_on: on },
      {
        onSuccess: () => {
          setText('');
          save.reset();
          toast.show(t('crm.history.added'));
        },
      },
    );

  return (
    <Section title={t('crm.history.title')}>
      {writable && (
        <form
          className={styles.noteForm}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <FormField label={t('crm.history.text')} error={errors.field.text ?? errors.general}>
            {(p) => (
              <Textarea {...p} rows={2} value={text} onChange={(e) => setText(e.target.value)} />
            )}
          </FormField>
          <div className={styles.noteActions}>
            <FormField label={t('crm.history.on')}>
              {(p) => <DatePicker {...p} value={on} onChange={setOn} max={todayIso()} />}
            </FormField>
            <Button type="submit" size="sm" loading={save.isPending} disabled={text.trim() === ''}>
              {t('crm.history.add')}
            </Button>
          </div>
        </form>
      )}
      {rows.length === 0 ? (
        !writable && (
          <Text tone="muted" className={styles.empty}>
            {t('crm.history.empty')}
          </Text>
        )
      ) : (
        <List>
          {rows.map((n) => (
            <ListRow
              key={n.id}
              title={<span className={styles.notes}>{n.text}</span>}
              description={[formatDate(n.noted_on), n.author].filter(Boolean).join(' · ')}
              actionsLabel={rowMenu.label(formatDate(n.noted_on))}
              actions={writable ? [rowMenu.remove(() => setDeleting(n))] : []}
            />
          ))}
        </List>
      )}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
        title={t('crm.history.deleteTitle')}
        description={deleting?.text}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.show(t('crm.history.deleted'));
              setDeleting(null);
            },
          })
        }
      />
    </Section>
  );
}
